import { Injectable } from '@angular/core';
import {HttpClient, HttpParameterCodec, HttpParams} from "@angular/common/http";
import {SettingsService} from "./settings.service";
import dayjs, {Dayjs} from "dayjs";
import {MissingUserCredentialsError} from "../var/errors";
import localForage from "localforage";
import {firstValueFrom} from "rxjs";
import {EventsService} from "./events.service";

export class HttpUrlEncodingCodec implements HttpParameterCodec {
  encodeKey(k: string): string {
    return standardEncoding(k);
  }

  encodeValue(v: string): string {
    return standardEncoding(v);
  }

  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  decodeValue(v: string) {
    return decodeURIComponent(v);
  }
}

function standardEncoding(v: string): string {
  return encodeURIComponent(v);
}

@Injectable({
  providedIn: 'root'
})
export class UserService {



  constructor(
    private events: EventsService,
    private http: HttpClient,
    private settings: SettingsService
  ) {
  }

  /**
   * HttpParams are set with the username and password
   * @returns Observable
   */
  async login(credentials: {username: string, password: string}) {
    const loginParam = new HttpParams({encoder: new HttpUrlEncodingCodec()})
      .set('Username', credentials.username)
      .set('Password', credentials.password);
    const tokenUrl = await this.settings.get('token-url');
    const data = await firstValueFrom(this.http.post(tokenUrl, loginParam)) as {token: any};
    // Set user-related login information
    await localForage.setItem('loginData', data.token);
  }

  async restore() {
    const token: any = await this.getToken();
    // session should be valid for at least 30s for the user to extend it
    if (token && dayjs(token.expirationTime).diff(dayjs(), 's') >= 30) {
      this.events.publish('user:restore');
      return true;
    }
    await this.logout(true);

    return false;
  }

  /**
   * @throws MissingUserCredentialsError
   */
  async extendToken(credentials?: any) {
    if (!credentials) {
      try {
        credentials = await localForage.getItem('credentials');
      } catch (err) {
        console.log(err);
      }
    }
    if (credentials) {
      try {
        const token: any = await this.getToken();
        if (token) {
          const params = new HttpParams({encoder: new HttpUrlEncodingCodec()})
            .set('Token', token)
            .set('Username', credentials.username)
            .set('Password', credentials.password);
          const value = (await firstValueFrom(this.http.put('/login/' + token, params))) as any;
          await localForage.setItem('loginData', value.token);
          this.events.publish('user:extend-token', value.token);
        }
      } catch (err) {
        console.log(err);
      }
    } else {
      throw new MissingUserCredentialsError();
    }
  }

  async logout(quiet: boolean = false) {
    try {
      await localForage.removeItem('loginData');
    } catch (err) {
      console.log('Could not remove login data: ', err);
    }

    if (!quiet) {
      this.events.publish('user:logout');
    }
  }

  /**
   * Checks whether the token is valid
   * @returns boolean
   */
  async isLoggedIn(): Promise<boolean> {
    // Returns true if the current timestamp is before the expiration date
    const loginData = await localForage.getItem<any>('loginData');
    return loginData && dayjs().isBefore(await this.getExpirationTime());
  }

  /**
   * Server request to check if the token is still valid
   * @returns Observable
   */
  async tokenIsValid() {
    const token = await this.getToken();
    const tokenUrl = await this.settings.get('token-url');
    return firstValueFrom(this.http.get(`${tokenUrl}/${token}`));
  }

  async getToken(): Promise<any> {
    const loginData = await localForage.getItem<any>('loginData');
    if (loginData) {
      return loginData.token;
    } else {
      return null;
    }
  }

  async getUserId(): Promise<string | undefined> {
    const loginData = await localForage.getItem<any>('loginData');
    if (loginData) {
      return loginData.userId;
    } else {
      return undefined;
    }
  }

  async getExpirationTime(): Promise<Dayjs | undefined> {
    const loginData = await localForage.getItem<any>('loginData');
    if (loginData) {
      return dayjs(loginData.expirationTime);
    } else {
      return undefined;
    }
  }
}
